import { apiClient } from './api-client';

// ==================== Enums ====================

export enum ApiKeyPermission {
  TICKETS_READ = 'tickets:read',
  TICKETS_WRITE = 'tickets:write',
  CUSTOMERS_READ = 'customers:read',
  CUSTOMERS_WRITE = 'customers:write',
  KNOWLEDGE_READ = 'knowledge:read',
  KNOWLEDGE_WRITE = 'knowledge:write',
  ANALYTICS_READ = 'analytics:read',
  // P1.4 — replaced the older catch-all `webhooks:manage` with the
  // two-scope shape the api-gateway actually enforces. Keep these in
  // sync with `bff/src/modules/api-keys/dto/api-keys.dto.ts` and
  // `api-gateway/src/modules/auth/api-key-scopes.ts`.
  WEBHOOKS_READ = 'webhooks:read',
  WEBHOOKS_WRITE = 'webhooks:write',
  USERS_READ = 'users:read',
  SETTINGS_READ = 'settings:read',
}

/** Matches tenant-service's productName limit. */
export const PRODUCT_NAME_MAX_LENGTH = 100;

// ==================== Interfaces ====================

export interface ApiKey {
  id: string;
  tenantId: string;
  name: string;
  description?: string;
  keyPrefix: string;
  permissions: ApiKeyPermission[];
  isActive: boolean;
  expiresAt?: string;
  lastUsedAt?: string;
  ipWhitelist?: string[];
  rateLimit?: number;
  /** Product label (e.g. "Control Tower"); tickets this key creates get origin = productSlug. */
  productName?: string | null;
  productSlug?: string | null;
  usageCount: number;
  /** Id of the user who created the key; null when it was never recorded. */
  createdBy: string | null;
  /** That user's name or email; null (or absent on an older BFF) when unknown. */
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiKeyWithSecret extends ApiKey {
  key: string;
}

export interface CreateApiKeyDto {
  name: string;
  description?: string;
  permissions: ApiKeyPermission[];
  expiresAt?: string;
  ipWhitelist?: string[];
  rateLimit?: number;
  productName?: string;
}

export interface UpdateApiKeyDto {
  name?: string;
  description?: string;
  permissions?: ApiKeyPermission[];
  isActive?: boolean;
  ipWhitelist?: string[];
  rateLimit?: number;
  /** A name sets the product; `null` clears it. */
  productName?: string | null;
}

export interface ApiKeyUsage {
  date: string;
  requests: number;
  errors: number;
  endpoints: Record<string, number>;
}

/** Requests and errors for one normalised route over the usage window. */
export interface ApiKeyEndpointUsage {
  endpoint: string;
  requests: number;
  errors: number;
}

/** One kind of failure (status + public error code) over the usage window. */
export interface ApiKeyErrorTypeUsage {
  statusCode: number;
  errorCode: string | null;
  count: number;
  lastOccurredAt: string;
  sampleMessage: string | null;
}

export interface ApiKeyUsageSummary {
  /** Per-day series (what the tiles and the bar chart are built from). */
  daily: ApiKeyUsage[];
  byEndpoint: ApiKeyEndpointUsage[];
  byError: ApiKeyErrorTypeUsage[];
  /** True when usage could not be read — the lists are empty for that reason. */
  unavailable?: boolean;
}

/** One request made with a key. Bodies and query strings are never recorded. */
export interface ApiKeyRequestLogEntry {
  id: string;
  occurredAt: string;
  method: string;
  path: string;
  endpoint: string;
  statusCode: number;
  errorCode: string | null;
  errorMessage: string | null;
  durationMs: number | null;
  ipAddress: string | null;
  requestId: string | null;
}

export interface ApiKeyRequestLogPage {
  data: ApiKeyRequestLogEntry[];
  /** Pass as `before` for the next (older) page; null when there is none. */
  nextBefore: string | null;
}

export interface ApiKeyRequestLogQuery {
  errorsOnly?: boolean;
  limit?: number;
  before?: string;
  requestId?: string;
}

/** How long the request log is kept (tenant-service purges older rows). */
/** Most allow-list entries a key may carry (tenant-service enforces the same). */
export const IP_ALLOW_LIST_MAX_ENTRIES = 100;

/**
 * `203.0.113.5`, `2001:db8::1`, `198.51.100.0/24`, `2001:db8::/32`. Mirrors
 * tenant-service's `isIpOrCidr` so a typo is caught before saving; the server
 * check is the one that counts.
 */
export function isIpOrCidr(value: string): boolean {
  const parts = value.split('/');
  if (parts.length > 2) return false;
  const [address, prefix] = parts;

  const version = isIPv4(address) ? 4 : isIPv6(address) ? 6 : 0;
  if (version === 0) return false;
  if (prefix === undefined) return true;
  if (!/^\d{1,3}$/.test(prefix)) return false;
  return Number(prefix) <= (version === 4 ? 32 : 128);
}

function isIPv4(value: string): boolean {
  const octets = value.split('.');
  return (
    octets.length === 4 &&
    octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255 && (o === '0' || !o.startsWith('0')))
  );
}

function isIPv6(value: string): boolean {
  if (!value.includes(':') || !/^[0-9a-fA-F:.]+$/.test(value)) return false;
  try {
    // The URL parser validates (and would normalise) an IPv6 literal.
    return new URL(`http://[${value}]/`).hostname.length > 2;
  } catch {
    return false;
  }
}

/**
 * Parse the allow-list text box: one entry per line (commas and spaces also
 * separate). Duplicates are dropped; entries that are not an IP or CIDR are
 * returned in `invalid` so the form can name them.
 */
export function parseIpAllowList(text: string): { entries: string[]; invalid: string[] } {
  const tokens = text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const unique = [...new Set(tokens)];
  return {
    entries: unique.filter(isIpOrCidr),
    invalid: unique.filter((t) => !isIpOrCidr(t)),
  };
}

export const REQUEST_LOG_RETENTION_DAYS = 14;
export const REQUEST_LOG_PAGE_SIZE = 50;

/** Where API-key callers reach the public API in this environment. */
export interface ApiIntegrationInfo {
  apiBaseUrl: string;
  apiReferenceUrl: string;
  openApiUrl: string;
}

/** Used when the BFF cannot say (older BFF, or the call failed). */
export const DEFAULT_PUBLIC_API_URL = 'https://api-gateway.centraone.ai';

export function integrationInfoFor(apiBaseUrl: string): ApiIntegrationInfo {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return {
    apiBaseUrl: base,
    apiReferenceUrl: `${base}/api/api-reference`,
    openApiUrl: `${base}/api/openapi.yaml`,
  };
}

// ==================== Service ====================

class ApiKeysService {
  /** The public API host for the integration guide; never throws. */
  async getIntegrationInfo(): Promise<ApiIntegrationInfo> {
    try {
      const response = await apiClient.get<Partial<ApiIntegrationInfo>>('/api-keys/integration-info');
      const base = response.data?.apiBaseUrl;
      if (typeof base === 'string' && /^https?:\/\//.test(base)) return integrationInfoFor(base);
    } catch {
      // fall through to the default
    }
    return integrationInfoFor(DEFAULT_PUBLIC_API_URL);
  }

  async getApiKeys(): Promise<ApiKey[]> {
    const response = await apiClient.get<ApiKey[]>('/api-keys');
    return response.data;
  }

  async getApiKey(id: string): Promise<ApiKey> {
    const response = await apiClient.get<ApiKey>(`/api-keys/${id}`);
    return response.data;
  }

  async createApiKey(dto: CreateApiKeyDto): Promise<ApiKeyWithSecret> {
    const response = await apiClient.post<ApiKeyWithSecret>('/api-keys', dto);
    return response.data;
  }

  async updateApiKey(id: string, dto: UpdateApiKeyDto): Promise<ApiKey> {
    const response = await apiClient.put<ApiKey>(`/api-keys/${id}`, dto);
    return response.data;
  }

  async deleteApiKey(id: string): Promise<void> {
    await apiClient.delete(`/api-keys/${id}`);
  }

  async rotateApiKey(id: string): Promise<ApiKeyWithSecret> {
    const response = await apiClient.post<ApiKeyWithSecret>(`/api-keys/${id}/rotate`);
    return response.data;
  }

  /**
   * Usage for a key over the last `days`: the per-day series plus the
   * by-endpoint and by-error breakdowns. A BFF that predates the breakdowns
   * answers with the bare per-day array; that is read as "no breakdowns".
   */
  async getApiKeyUsageSummary(id: string, days: number = 7): Promise<ApiKeyUsageSummary> {
    const response = await apiClient.get<ApiKeyUsageSummary | ApiKeyUsage[]>(
      `/api-keys/${id}/usage?days=${days}`,
    );
    const body = response.data;
    if (Array.isArray(body)) return { daily: body, byEndpoint: [], byError: [] };
    return {
      daily: Array.isArray(body?.daily) ? body.daily : [],
      byEndpoint: Array.isArray(body?.byEndpoint) ? body.byEndpoint : [],
      byError: Array.isArray(body?.byError) ? body.byError : [],
      unavailable: body?.unavailable === true,
    };
  }

  /** Just the per-day series (see `getApiKeyUsageSummary`). */
  async getApiKeyUsage(id: string, days: number = 7): Promise<ApiKeyUsage[]> {
    return (await this.getApiKeyUsageSummary(id, days)).daily;
  }

  /**
   * Recent requests made with a key, newest first. Blank filters are left out
   * of the query rather than sent empty.
   */
  async getApiKeyRequests(
    id: string,
    query: ApiKeyRequestLogQuery = {},
  ): Promise<ApiKeyRequestLogPage> {
    const params: Record<string, string | number | boolean> = {};
    if (query.errorsOnly) params.errorsOnly = true;
    if (query.limit !== undefined) params.limit = query.limit;
    if (query.before) params.before = query.before;
    const requestId = query.requestId?.trim();
    if (requestId) params.requestId = requestId;

    const response = await apiClient.get<ApiKeyRequestLogPage>(`/api-keys/${id}/requests`, {
      params,
    });
    return {
      data: Array.isArray(response.data?.data) ? response.data.data : [],
      nextBefore: response.data?.nextBefore ?? null,
    };
  }

  // ==================== Helpers ====================

  getPermissionLabel(permission: ApiKeyPermission): string {
    const labels: Record<ApiKeyPermission, string> = {
      [ApiKeyPermission.TICKETS_READ]: 'Read Tickets',
      [ApiKeyPermission.TICKETS_WRITE]: 'Write Tickets',
      [ApiKeyPermission.CUSTOMERS_READ]: 'Read Customers',
      [ApiKeyPermission.CUSTOMERS_WRITE]: 'Write Customers',
      [ApiKeyPermission.KNOWLEDGE_READ]: 'Read Knowledge Base',
      [ApiKeyPermission.KNOWLEDGE_WRITE]: 'Write Knowledge Base',
      [ApiKeyPermission.ANALYTICS_READ]: 'Read Analytics',
      [ApiKeyPermission.WEBHOOKS_READ]: 'Read Webhooks',
      [ApiKeyPermission.WEBHOOKS_WRITE]: 'Manage Webhooks',
      [ApiKeyPermission.USERS_READ]: 'Read Users',
      [ApiKeyPermission.SETTINGS_READ]: 'Read Settings',
    };
    return labels[permission] || permission;
  }

  getPermissionDescription(permission: ApiKeyPermission): string {
    const descriptions: Record<ApiKeyPermission, string> = {
      [ApiKeyPermission.TICKETS_READ]: 'View tickets, comments, and attachments',
      [ApiKeyPermission.TICKETS_WRITE]: 'Create, update, and close tickets',
      [ApiKeyPermission.CUSTOMERS_READ]: 'View customer and organization data',
      [ApiKeyPermission.CUSTOMERS_WRITE]: 'Create and update customers',
      [ApiKeyPermission.KNOWLEDGE_READ]: 'View knowledge base articles',
      [ApiKeyPermission.KNOWLEDGE_WRITE]: 'Create and edit articles',
      [ApiKeyPermission.ANALYTICS_READ]: 'Access analytics and reports',
      [ApiKeyPermission.WEBHOOKS_READ]: 'View webhook configurations and delivery history',
      [ApiKeyPermission.WEBHOOKS_WRITE]: 'Create, update, and delete webhook endpoints',
      [ApiKeyPermission.USERS_READ]: 'View user information',
      [ApiKeyPermission.SETTINGS_READ]: 'View account settings',
    };
    return descriptions[permission] || '';
  }

  getPermissionCategory(permission: ApiKeyPermission): string {
    if (permission.startsWith('tickets:')) return 'Tickets';
    if (permission.startsWith('customers:')) return 'Customers';
    if (permission.startsWith('knowledge:')) return 'Knowledge Base';
    if (permission.startsWith('analytics:')) return 'Analytics';
    if (permission.startsWith('webhooks:')) return 'Webhooks';
    if (permission.startsWith('users:')) return 'Users';
    if (permission.startsWith('settings:')) return 'Settings';
    return 'Other';
  }

  getAllPermissions(): ApiKeyPermission[] {
    return Object.values(ApiKeyPermission);
  }

  getGroupedPermissions(): Record<string, ApiKeyPermission[]> {
    const permissions = this.getAllPermissions();
    return permissions.reduce((groups, permission) => {
      const category = this.getPermissionCategory(permission);
      if (!groups[category]) groups[category] = [];
      groups[category].push(permission);
      return groups;
    }, {} as Record<string, ApiKeyPermission[]>);
  }

  formatKeyPrefix(prefix: string): string {
    return `${prefix}...`;
  }

  isExpired(expiresAt?: string): boolean {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  }

  getExpiryStatus(expiresAt?: string): { label: string; color: string } {
    if (!expiresAt) return { label: 'Never expires', color: 'text-gray-500' };

    const expiry = new Date(expiresAt);
    const now = new Date();
    const daysUntil = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (daysUntil < 0) return { label: 'Expired', color: 'text-red-600' };
    if (daysUntil === 0) return { label: 'Expires today', color: 'text-orange-600' };
    if (daysUntil <= 7) return { label: `Expires in ${daysUntil} days`, color: 'text-yellow-600' };
    if (daysUntil <= 30) return { label: `Expires in ${daysUntil} days`, color: 'text-gray-600' };
    return { label: `Expires ${expiry.toLocaleDateString()}`, color: 'text-gray-500' };
  }

  formatUsageCount(count: number): string {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
    return String(count);
  }

  getLastUsedLabel(lastUsedAt?: string): string {
    if (!lastUsedAt) return 'Never used';

    const lastUsed = new Date(lastUsedAt);
    const now = new Date();
    const diffMs = now.getTime() - lastUsed.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return lastUsed.toLocaleDateString();
  }

  /** Who created a key, for display: the name/email when known, else "Unknown". */
  getCreatedByLabel(key: Pick<ApiKey, 'createdByName'>): string {
    return key.createdByName?.trim() || 'Unknown';
  }

  /** Share of requests that failed, as a percentage (0 when there were none). */
  getErrorRate(requests: number, errors: number): number {
    return requests > 0 ? (errors / requests) * 100 : 0;
  }

  /** "just now", "5 min ago", "3 h ago", "2 d ago" — compact, for table cells. */
  formatRelativeTime(iso: string, now: Date = new Date()): string {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '—';
    const seconds = Math.max(0, Math.floor((now.getTime() - then) / 1000));
    if (seconds < 45) return 'just now';
    const minutes = Math.round(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours} h ago`;
    return `${Math.round(hours / 24)} d ago`;
  }

  /** Full local date and time, to the second — the hover text for a relative time. */
  formatExactTime(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
  }

  formatDuration(durationMs: number | null | undefined): string {
    if (durationMs === null || durationMs === undefined) return '—';
    if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
    return `${(durationMs / 1000).toFixed(durationMs < 10000 ? 2 : 1)} s`;
  }

  /** Trimmed product name, or undefined when blank (the API rejects ""). */
  normalizeProductName(value: string | null | undefined): string | undefined {
    const trimmed = (value ?? '').trim();
    return trimmed ? trimmed.slice(0, PRODUCT_NAME_MAX_LENGTH) : undefined;
  }

  /**
   * Whether this user may see a key's request log (caller IPs, error details):
   * admins only. Tenant roles below admin are refused; admin, owner and
   * platform staff managing the tenant are allowed. Mirrors the BFF and
   * tenant-service checks, which are the ones that actually enforce it.
   */
  canViewRequestLog(role: string | null | undefined): boolean {
    if (typeof role !== 'string' || role.trim() === '') return false;
    return !['light_agent', 'agent', 'supervisor'].includes(role.trim().toLowerCase());
  }

  /**
   * Distinct product names already used on this tenant's keys, for the
   * "Product" field's suggestions — keys for the same product must share the
   * exact name (it becomes the ticket source), so offering the existing
   * spelling avoids "Control Tower" vs "ControlTower" splitting one product
   * into two sources. Case-insensitive de-dupe keeps the first spelling seen.
   */
  getProductSuggestions(keys: Pick<ApiKey, 'productName'>[]): string[] {
    const byLower = new Map<string, string>();
    for (const key of keys) {
      const name = this.normalizeProductName(key.productName);
      if (name && !byLower.has(name.toLowerCase())) byLower.set(name.toLowerCase(), name);
    }
    return [...byLower.values()].sort((a, b) => a.localeCompare(b));
  }
}

export const apiKeysService = new ApiKeysService();
export default apiKeysService;
